import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { SuccessResponse } from '@interfaces/central-monitoring';
import {
    CreateObservationModel,
    DatasetResponseDto,
    DeleteObservationModel,
    ObservationBulkUpdateDto,
    ObservationDetailsDto,
    ObservationDto,
    ObservationType,
    UpdateObservationModel,
} from '@interfaces-clean/observations.interface';
import { getData, postData } from '@apis/rest-client';

const observationUrl = 'CentralMonitoring/Observation';

const ObservationsApi = {
    useSave: (studyRef: string) => {
        return useSWRMutation(
            `${observationUrl}/Add/${studyRef}`,
            (url: string, { arg }: { arg: CreateObservationModel }) => {
                return postData<CreateObservationModel, SuccessResponse>(url, { arg, timeout: 30000 });
            },
        );
    },
    useUpdate: (studyRef: string) => {
        return useSWRMutation(
            `${observationUrl}/Update/${studyRef}`,
            (url: string, { arg }: { arg: UpdateObservationModel }) => {
                return postData<UpdateObservationModel, SuccessResponse>(url, { arg, timeout: 30000 });
            },
        );
    },
    useDelete: (studyRef: string) => {
        return useSWRMutation(
            `${observationUrl}/Delete/${studyRef}`,
            (url: string, { arg }: { arg: DeleteObservationModel }) => {
                return postData<DeleteObservationModel, boolean>(url, { arg, timeout: 30000 });
            },
        );
    },
    useGet: (studyRef: string, id: string) => {
        return useSWR(`${observationUrl}/${studyRef}/${id}`, (url: string) => {
            return getData<ObservationDto>(url, { timeout: 30000 });
        });
    },
    useGetDetails: (studyRef: string, id: string, observationType: ObservationType) => {
        return useSWR(`${observationUrl}/${studyRef}/${id}/${observationType}/Details`, (url: string) => {
            return getData<ObservationDetailsDto>(url, { timeout: 30000 });
        });
    },
    useBulkUpdate: (studyRef: string) => {
        return useSWRMutation(
            `${observationUrl}/bulkUpdate/${studyRef}`,
            (url: string, { arg }: { arg: ObservationBulkUpdateDto }) => {
                return postData<ObservationBulkUpdateDto, boolean>(url, { arg, timeout: 30000 });
            },
        );
    },
    useGetDatasets: (studyRef: string, observationId: string, observationType: ObservationType) => {
        return useSWR(`${observationUrl}/Datasets/${observationType}/${studyRef}/${observationId}`, (url: string) => {
            return getData<DatasetResponseDto[]>(url, { timeout: 30000 });
        });
    },
    useHasClosedBeforeLatestReview: (studyRef: string) => {
        return useSWRMutation(
            `${observationUrl}/HasClosedBeforeLatestReview/${studyRef}`,
            (url: string, { arg }: { arg: number[] }) => {
                return postData<number[], boolean>(url, { arg, timeout: 30000 });
            },
        );
    },
    GetObservationAutomationStatus: (studyRef: string, id: string, enabled: boolean = true) => {
        return useSWR(
            enabled ? `${observationUrl}/GetObservationAutomationStatus/${studyRef}/${id}/` : null,
            (url: string) => {
                return getData<boolean>(url, { timeout: 30000 });
            },
        );
    },
    GetIntegrationStatus: (studyRef: string, observationType: ObservationType) => {
        let fullEndpoint: string | null = null;
        switch (observationType) {
            case ObservationType.Site:
                fullEndpoint = `${observationUrl}/IntegrationStatus/${studyRef}/Site`;
                break;
            case ObservationType.Study:
                fullEndpoint = null;
                break;
            case ObservationType.Controls:
                fullEndpoint = null;
                break;
            default:
        }

        return useSWR<string>(fullEndpoint, (url: string) => {
            return getData<string>(url, { timeout: 30000 });
        });
    },
};

export default ObservationsApi;
